import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}

function formatDateTime(dt: string | null): string {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatDuration(ms: number): string {
  const min = Math.floor(ms / 60000);
  const sec = Math.floor((ms % 60000) / 1000);
  return `${min} Min. ${sec} Sek.`;
}

export function AdminAuctionAnalyticsPage() {
  const { aktenzeichen } = useParams<{ aktenzeichen: string }>();
  const [auction, setAuction] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [viewerData, setViewerData] = useState<any[]>([]);
  const [sectionData, setSectionData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!aktenzeichen) return;
    loadData();
  }, [aktenzeichen]);

  async function loadData() {
    const { data: a } = await supabase.from('auctions').select('id, aktenzeichen, titel').eq('aktenzeichen', aktenzeichen).single();
    if (!a) { setLoading(false); return; }
    setAuction(a);

    const { data: viewStats } = await supabase
      .from('auction_view_stats')
      .select('*')
      .eq('auction_id', a.id)
      .single();

    const { data: pvData } = await supabase
      .from('page_views')
      .select('profile_id, session_id, viewed_at, ip_adresse')
      .eq('auction_id', a.id)
      .order('viewed_at', { ascending: false });

    const { data: focusData } = await supabase
      .from('section_focus_events')
      .select('section_id, focus_seconds')
      .eq('page_view_id', pvData?.[0]?.id || '');

    const { data: viewerRows } = await supabase
      .from('page_views')
      .select(`
        profile_id,
        ip_adresse,
        companies!inner (name, ansprechpartner_vorname, ansprechpartner_nachname),
        section_focus_events (section_id, focus_seconds)
      `)
      .eq('auction_id', a.id)
      .order('viewed_at', { ascending: false });

    setViewerData(viewerRows || []);
    setSectionData(focusData || []);
    setStats(viewStats);
    setLoading(false);
  }

  if (loading) return <div className="container-content py-12"><p className="text-text-secondary">Laden...</p></div>;
  if (!auction) return <div className="container-content py-12"><p className="text-text-secondary">Auktion nicht gefunden.</p></div>;

  return (
    <div className="container-content py-12">
      <nav className="text-sm text-text-muted mb-4">
        <Link to="/admin" className="">Admin</Link> <span className="mx-2">/</span>
        <Link to="/admin/auktionen" className="">Auktionen</Link> <span className="mx-2">/</span>
        <Link to={`/admin/auktionen/${auction.aktenzeichen}`} className="">{auction.aktenzeichen}</Link>
        <span className="mx-2">/</span>
        <span>Analytics</span>
      </nav>

      <h1 className="text-2xl font-serif mb-6">Analytics — {auction.aktenzeichen}</h1>

      {/* Block A: Kenzahlen */}
      <h2 className="text-lg font-serif mb-3">Kenntzahlen</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <StatCard label="Gesamt-Aufrufe" value={stats?.total_views || 0} />
        <StatCard label="Eindeutige Betrachter" value={stats?.unique_viewers || 0} />
        <StatCard label="Eindeutige Sessions" value={stats?.unique_sessions || 0} />
        <StatCard label="Letzter Aufruf" value={stats?.last_view_at ? formatDateTime(stats.last_view_at) : '—'} />
      </div>

      {/* Block B: Viewer-Tabelle */}
      <h2 className="text-lg font-serif mb-3">Wer hat wie oft angeschaut</h2>
      {viewerData.length === 0 ? (
        <p className="text-text-secondary text-sm mb-8">Keine Daten vorhanden.</p>
      ) : (
        <div className="overflow-x-auto mb-8">
          <table className="min-w-full">
            <thead><tr><th>Bieter</th><th>Unternehmen</th><th>IP</th><th>Aufrufe</th><th>Letzter Aufruf</th></tr></thead>
            <tbody>
              {viewerData.map((v: any, i) => (
                <tr key={i}>
                  <td>{v.companies?.ansprechpartner_vorname} {v.companies?.ansprechpartner_nachname}</td>
                  <td>{v.companies?.name}</td>
                  <td className="font-mono text-xs">{v.ip_adresse}</td>
                  <td>—</td>
                  <td>{formatDateTime(v.viewed_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Block C: Section-Fokus */}
      <h2 className="text-lg font-serif mb-3">Fokuszeit pro Abschnitt</h2>
      {sectionData.length === 0 ? (
        <p className="text-text-secondary text-sm">Keine Daten vorhanden.</p>
      ) : (
        <table className="min-w-full">
          <thead><tr><th>Abschnitt</th><th>Ø Fokuszeit</th><th>Gesamt Fokuszeit</th></tr></thead>
          <tbody>
            {groupSections(sectionData).map(s => (
              <tr key={s.section_id}>
                <td>{s.section_id}</td>
                <td>{formatDuration(s.avgMs)}</td>
                <td>{formatDuration(s.totalMs)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return <div className="p-4 border border-surface-border rounded-sm"><div className="text-sm text-text-muted">{label}</div><div className="font-serif">{value}</div></div>;
}

function groupSections(data: any[]): Array<{ section_id: string; avgMs: number; totalMs: number }> {
  const map: Record<string, number[]> = {};
  for (const d of data) {
    if (!map[d.section_id]) map[d.section_id] = [];
    map[d.section_id].push(d.focus_seconds * 1000);
  }
  return Object.entries(map).map(([id, times]) => ({
    section_id: id,
    avgMs: times.reduce((a, b) => a + b, 0) / times.length,
    totalMs: times.reduce((a, b) => a + b, 0),
  }));
}
